"use client";

import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import { formSlot, type FormTheme } from "./styles";

export type SelectOption = {
  value: string;
  label: string;
  hint?: string;
  /** Stays in the list whatever is searched: «Nuovo…» is found by not finding anything else. */
  always?: boolean;
};

export type SelectProps = {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  theme: FormTheme;
  /** Accessible name (the visible label is rendered by the caller). */
  ariaLabel: string;
};

/**
 * A small styled select: a button showing the choice and a list under it, with a search
 * field on top. Closes on a click outside and on Escape; typing filters, arrows move, Enter
 * picks.
 */
/** The search row stays on top while the list scrolls under it. */
const SEARCH_ROW: CSSProperties = {
  position: "sticky",
  top: -4,
  margin: "-4px -4px 4px",
  padding: 4,
  background: "inherit",
  zIndex: 1,
};
const SEARCH: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid rgba(15,23,42,.12)",
  borderRadius: 8,
  padding: "8px 10px",
  font: "inherit",
  fontSize: 14,
  background: "rgba(15,23,42,.03)",
  color: "inherit",
  outline: "none",
};

export function Select({
  value,
  options,
  onChange,
  placeholder = "Scegli…",
  disabled,
  theme,
  ariaLabel,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [query, setQuery] = useState("");
  const root = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);
  const wanted = query.trim().toLowerCase();
  const listed = wanted
    ? options.filter(
        (o) =>
          o.always ||
          o.label.toLowerCase().includes(wanted) ||
          o.value.toLowerCase().includes(wanted),
      )
    : options;

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const pick = (option: SelectOption | undefined) => {
    if (!option) return;
    onChange(option.value);
    setOpen(false);
  };

  const onKey = (event: KeyboardEvent) => {
    if (event.key === "Escape") return setOpen(false);
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        setQuery("");
        setActive(Math.max(0, options.indexOf(selected as SelectOption)));
        return setOpen(true);
      }
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((i) => (i + step + listed.length) % Math.max(1, listed.length));
    }
    if (event.key === "Enter" && open) {
      event.preventDefault();
      pick(listed[active]);
    }
  };

  return (
    <div ref={root} style={{ position: "relative" }} onKeyDown={onKey}>
      <button
        type="button"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={disabled}
        {...formSlot(theme, "input", {
          cursor: disabled ? "default" : "pointer",
          display: "flex",
          alignItems: "center",
        })}
        onClick={() => {
          setQuery("");
          setActive(Math.max(0, options.indexOf(selected as SelectOption)));
          setOpen((o) => !o);
        }}
      >
        <span
          style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {selected ? selected.label : <span style={{ opacity: 0.5 }}>{placeholder}</span>}
        </span>
        <span aria-hidden style={{ opacity: 0.5, marginLeft: 8 }}>
          ▾
        </span>
      </button>
      {open ? (
        <ul role="listbox" aria-label={ariaLabel} {...formSlot(theme, "menu")}>
          <li role="presentation" style={SEARCH_ROW}>
            <input
              autoFocus
              aria-label={`Cerca: ${ariaLabel}`}
              placeholder="Cerca…"
              value={query}
              spellCheck={false}
              onChange={(event) => {
                setQuery(event.target.value);
                setActive(0);
              }}
              style={SEARCH}
            />
          </li>
          {listed.length === 0 ? (
            <li role="presentation" style={{ padding: "9px 12px", opacity: 0.5, fontSize: 14 }}>
              Nessun risultato
            </li>
          ) : null}
          {listed.map((option, i) => (
            <li
              key={option.value}
              role="option"
              aria-selected={option.value === value}
              {...formSlot(theme, "option", {
                background: i === active ? "rgba(15,23,42,.06)" : undefined,
                fontWeight: option.value === value ? 700 : undefined,
              })}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(event) => {
                event.preventDefault();
                pick(option);
              }}
            >
              <span>{option.label}</span>
              {option.hint ? <span style={{ opacity: 0.5 }}>{option.hint}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
